import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { pget, ppost, fmtDate } from "./portalApi";
import { BarChart3, Check, Trophy } from "lucide-react";

export default function PortalSurveys() {
  const [items, setItems] = useState([]);
  const [closed, setClosed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");

  const load = useCallback(() => {
    pget("/resident/surveys").then((r) => { setItems(r.data.items || []); setClosed(r.data.closed || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const vote = async (s, opt) => {
    setSaving(s.id + opt.key);
    try {
      const r = await ppost(`/resident/surveys/${s.id}/vote`, { option: opt.key });
      toast.success(r.data.confirmation || `Vote recorded: ${r.data.option_text}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't record that vote"); }
    finally { setSaving(""); }
  };

  return (
    <div className="space-y-6" data-testid="portal-surveys-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><BarChart3 className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Surveys</h1>
        <p className="text-muted-foreground mt-1">Your community wants your opinion — one vote per household member, change it anytime while voting is open.</p>
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>
        : items.length === 0 && closed.length === 0 ? <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center text-muted-foreground">No open surveys right now.</div>
        : <>
        {items.length === 0 ? <div className="bg-white rounded-2xl border border-sand-200 p-8 text-center text-muted-foreground text-sm">No open surveys right now.</div>
        : <div className="space-y-4" data-testid="portal-surveys-list">
          {items.map((s) => {
            const total = s.results ? Object.values(s.results).reduce((a, b) => a + b, 0) : 0;
            return (
              <div key={s.id} data-testid={`portal-survey-${s.id}`} className="bg-white rounded-2xl border border-sand-200 p-5">
                <div className="font-heading font-bold text-ink">{s.title}</div>
                {s.description && <p className="text-sm text-ink-600 mt-1">{s.description}</p>}
                {s.to_date && <p className="text-xs text-muted-foreground mt-1">Voting open until {fmtDate(s.to_date, { month: "long", day: "numeric" })}</p>}
                <div className="mt-3 space-y-2">
                  {(s.options || []).map((o) => {
                    const chosen = s.my_vote === o.key;
                    const n = s.results ? (s.results[o.key] || 0) : null;
                    const pct = total ? Math.round((n / total) * 100) : 0;
                    return (
                      <button key={o.key} data-testid={`portal-vote-${s.id}-${o.key}`} disabled={saving === s.id + o.key} onClick={() => vote(s, o)}
                        className={`relative w-full overflow-hidden rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-colors ${chosen ? "border-redline-500 bg-redline-50 text-ink" : "border-sand-200 bg-white text-ink-700 hover:border-ink"}`}>
                        {s.results != null && <span className="absolute inset-y-0 left-0 bg-sand-100/80 -z-0" style={{ width: `${pct}%` }} />}
                        <span className="relative flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">{chosen && <Check className="h-4 w-4 text-redline-600" />}<span><b>{o.key})</b> {o.text}</span></span>
                          {s.results != null && <span className="text-xs text-muted-foreground font-bold">{pct}%</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {s.my_vote && <p className="text-xs text-emerald-700 font-semibold mt-2">Your vote is in — tap another option to change it.</p>}
              </div>
            );
          })}
        </div>}

        {/* Final results — closed surveys stay visible so everyone sees the outcome */}
        {closed.length > 0 && (
          <div className="space-y-3 pt-2" data-testid="portal-survey-results">
            <h2 className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2">
              <Trophy className="h-5 w-5 text-redline-500" strokeWidth={1.75} /> Results
            </h2>
            {closed.map((s) => {
              const total = s.results ? Object.values(s.results).reduce((a, b) => a + b, 0) : 0;
              const top = Math.max(0, ...(s.options || []).map((o) => (s.results?.[o.key] || 0)));
              return (
                <div key={s.id} data-testid={`portal-survey-closed-${s.id}`} className="bg-white rounded-2xl border border-sand-200 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-heading font-bold text-ink">{s.title}</div>
                    <span className="rounded-full bg-sand-100 text-ink-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0">Voting ended</span>
                  </div>
                  {s.closed_at && <p className="text-xs text-muted-foreground mt-1">Closed {fmtDate(s.closed_at.slice(0, 10), { month: "long", day: "numeric" })} · {total} vote{total === 1 ? "" : "s"}</p>}
                  <div className="mt-3 space-y-2">
                    {(s.options || []).map((o) => {
                      const n = s.results?.[o.key] || 0;
                      const pct = total ? Math.round((n / total) * 100) : 0;
                      const winner = total > 0 && n === top;
                      const mine = s.my_vote === o.key;
                      return (
                        <div key={o.key} className={`relative overflow-hidden rounded-xl border px-4 py-2.5 text-sm ${winner ? "border-redline-300 bg-redline-50/50" : "border-sand-200 bg-white"}`}>
                          <span className={`absolute inset-y-0 left-0 ${winner ? "bg-redline-100/70" : "bg-sand-100/80"}`} style={{ width: `${pct}%` }} />
                          <span className="relative flex items-center justify-between gap-2">
                            <span className={`flex items-center gap-2 ${winner ? "font-bold text-ink" : "text-ink-700"}`}>
                              {winner && <Trophy className="h-3.5 w-3.5 text-redline-600" />}<span><b>{o.key})</b> {o.text}</span>
                              {mine && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-bold">Your vote</span>}
                            </span>
                            <span className="text-xs font-bold text-ink-600">{n} · {pct}%</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>}
    </div>
  );
}
